import { type ReactNode } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '../components/ui/button';
import { useAuth } from '../features/auth/auth-context';
import { MyIssuesPage } from '../features/issues/components/my-issues-page';
import { MyClientRequests } from '../features/issues/components/my-client-requests';

/**
 * Portal của khách hàng — APM-ISSUE-n, docs/plan-issue-management.md §8.2.
 *
 * ## ⚠️ Không dùng lại màn hình nội bộ, và đó là quyết định về bảo mật
 *
 * Một màn vừa phục vụ người trong vừa phục vụ người ngoài là một màn mà **mỗi
 * lần thêm một cột**, ai đó phải nhớ hỏi "khách có được thấy cột này không".
 * Câu hỏi ấy sẽ được nhớ vài lần đầu rồi thôi. Portal vì thế là một cây riêng:
 * nó chỉ hiện những gì nó tự dựng, nên thêm một cột vào bảng nội bộ **không
 * thể** rò ra đây.
 *
 * Route nằm ngoài `_app` cũng vì thế: `AppShell` mang menu điều hướng nội bộ,
 * `QuickUpdateFab` và `GuidedTour` — cả ba đều nói về một sản phẩm mà khách
 * hàng không dùng.
 *
 * ## Phạm vi bản đầu, cố ý hẹp
 *
 * Xem vấn đề mình đã báo, báo vấn đề mới, xác nhận đã xong, mở lại nếu chưa.
 * Không báo cáo, không thấy người xử lý là ai.
 *
 * APM-ISX-e: thân trang là `MyIssuesPage` dùng chung với `/issues` của nhân viên —
 * gom mọi dự án được mời, thêm issue **được chia sẻ** (người theo dõi), lịch sử
 * trạng thái kèm lời nhắn công khai. Component ấy vẫn chỉ hiện những gì API của
 * trang này trả, nên lý lẽ "không dùng lại màn hình nội bộ" ở trên giữ nguyên.
 */
/**
 * `issue` trên URL — link trong thông báo (APM-ISX-c) mở đúng issue. uuid không
 * JSON hoá được nên tới schema dưới dạng chuỗi.
 */
const PortalSearch = z.object({ issue: z.uuid().optional().catch(undefined) });

export const Route = createFileRoute('/portal')({
  validateSearch: PortalSearch,
  component: Portal,
});

function Portal(): ReactNode {
  const { t } = useTranslation();
  const { status, user, logout } = useAuth();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();

  if (status === 'restoring') {
    return (
      <div className="flex min-h-dvh items-center justify-center text-sm text-muted-foreground">
        {t('auth.restoringSession')}
      </div>
    );
  }

  if (status === 'anonymous' || !user) {
    void navigate({ to: '/login', replace: true });
    return null;
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-5xl flex-col gap-6 px-4 py-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">{t('portal.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('portal.greeting', { name: user.displayName })}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void logout()}>
          {t('common.signOut')}
        </Button>
      </header>

      {/* Đơn xin vào dự án qua link, còn chờ PM duyệt (APM-CLINV-c). */}
      <MyClientRequests />

      {/* Tổng hợp mọi dự án được mời (APM-ISX-e) — thay bảng theo từng dự án của bản đầu. */}
      <MyIssuesPage
        audience="client"
        openIssueId={search.issue ?? null}
        onOpenIssue={(issue) => void navigate({ search: issue ? { issue } : {} })}
      />
    </div>
  );
}
